// src/pages/grid/GridImport.tsx
// Import a class list (CSV) into an existing grid. Creating a new grid is done in
// the guided "Build a Grid" wizard; this page is the "add classes to a grid I
// already have" path, and (when the CSV has a Line column) records line availability.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UploadCloud, FileText, Info, Download, ArrowRight, Grid, Plus } from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import { useToast } from '../../contexts/ToastContext';
import { useGrid } from '../../contexts/GridContext';
import { GridImportModal } from '../../components/import/GridImportModal';

const GridImport: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { activeGrid, grids, loading } = useGrid();
  const [showImportModal, setShowImportModal] = useState(false);

  // Download a class-list template matching what the importer expects:
  // Code, Name, Faculty, and an optional Line column.
  const downloadTemplateCSV = () => {
    const header = 'Code,Name,Faculty,Line\n';
    const sampleRows = [
      'DIARTMADA,DISCOVER - ARTS - MEDIA ART & DESIGN (MAD),Arts,A',
      'DIENGENGA,DISCOVER - ENGLISH,English,B',
      'DIHUMHUMA,DISCOVER - HUMANITIES,Humanities,C',
      'DIMATMATA,DISCOVER - MATHS,Maths,',
    ].join('\n');
    const blob = new Blob([header + sampleRows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'class_list_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    addToast('Class list template downloaded', 'success');
  };

  return (
    <div>
      <PageHeader
        title="Import a Class List"
        description="Add classes to an existing grid from a CSV. To create a new grid, use Build a Grid."
        actions={
          <div className="flex space-x-2">
            <Button variant="secondary" leftIcon={<Download className="h-4 w-4" />} onClick={downloadTemplateCSV}>
              Download Template
            </Button>
            <Button
              variant="primary"
              leftIcon={<UploadCloud className="h-4 w-4" />}
              onClick={() => setShowImportModal(true)}
              disabled={loading || !activeGrid}
            >
              Import CSV
            </Button>
          </div>
        }
      />

      {/* Active grid status */}
      {activeGrid ? (
        <Card className="mb-6">
          <div className="p-4 bg-ok-bg border-l-4 border-ok-border">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-ok-fg">Active grid ready</h3>
                <p className="text-sm text-ok-fg">
                  <strong>{activeGrid.name}</strong> — {activeGrid.lines.length} lines,{' '}
                  {activeGrid.classes.length} classes
                </p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Grid className="h-4 w-4" />}
                onClick={() => navigate('/grid/multi-editor')}
              >
                Open Timetable Editor
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        <Card className="mb-6">
          <div className="p-4 bg-warn-bg border-l-4 border-warn-border">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-warn-fg">No grid yet</h3>
                <p className="text-sm text-warn-fg">
                  Create a grid first with Build a Grid, then come back to import your class list.
                </p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => navigate('/grid/setup')}
              >
                Build a Grid
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* How it works */}
      <Card className="mb-6">
        <div className="p-6">
          <h3 className="text-card-title text-ink-900 mb-4">How importing works</h3>
          <div className="bg-navy-50 border border-navy-200 rounded-md p-4">
            <div className="flex">
              <Info className="h-5 w-5 text-navy-500 mr-3 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-navy-800 mb-1">Class list format</h4>
                <ul className="text-sm text-navy-700 space-y-1 list-disc pl-5">
                  <li>One class per row, with columns <strong>Code, Name, Faculty</strong></li>
                  <li>Optional <strong>Line</strong> column sets which line each class runs on</li>
                  <li>Classes are added to the grid you choose during import</li>
                  <li>Teachers are assigned afterwards in the Timetable Editor</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Button variant="secondary" leftIcon={<FileText className="h-4 w-4" />} onClick={downloadTemplateCSV}>
              Download Template
            </Button>
            <Button
              variant="primary"
              leftIcon={<UploadCloud className="h-4 w-4" />}
              onClick={() => setShowImportModal(true)}
              disabled={loading || !activeGrid}
            >
              {!activeGrid ? 'Create a grid first' : 'Import CSV'}
            </Button>
          </div>
        </div>
      </Card>

      {/* Current grids summary */}
      {grids.length > 0 && (
        <Card className="mb-6">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-card-title text-ink-900">Your grids</h3>
              <Button
                variant="ghost"
                rightIcon={<ArrowRight className="h-4 w-4" />}
                onClick={() => navigate('/grid/multi-editor')}
              >
                Open Timetable Editor
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {grids.slice(0, 3).map((grid) => {
                const isActive = activeGrid?.id === grid.id;
                return (
                  <div
                    key={grid.id}
                    className={`border rounded-lg p-4 ${isActive ? 'ring-2 ring-navy-500 bg-navy-50' : ''}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-ink-900">{grid.name}</h4>
                      {isActive && (
                        <span className="px-2 py-1 text-xs bg-navy-100 text-navy-800 rounded font-medium">
                          Active
                        </span>
                      )}
                    </div>
                    <p className="font-mono tabular-nums text-sm text-ink-500 mb-3">
                      {grid.classes.length} classes • {grid.lines.length} lines
                    </p>
                    <Button variant="ghost" size="sm" onClick={() => navigate('/grid/multi-editor')}>
                      Edit →
                    </Button>
                  </div>
                );
              })}
            </div>
            {grids.length > 3 && (
              <div className="mt-4 text-center">
                <Button variant="ghost" onClick={() => navigate('/grid/multi-editor')}>
                  View all {grids.length} grids in the editor →
                </Button>
              </div>
            )}
          </div>
        </Card>
      )}

      <GridImportModal isOpen={showImportModal} onClose={() => setShowImportModal(false)} />
    </div>
  );
};

export default GridImport;
